package com.zq.webproject.ui.withdraw

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.zq.webproject.model.WithdrawRecord
import com.zq.webproject.network.Repository
import com.zq.webproject.utils.TokenManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch


/**
 * 提现页面ViewModel
 * 管理用户余额和提现记录
 */
class WithdrawViewModel {
    private val repository = Repository()
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    // 用户余额
    var balance by mutableStateOf<Double?>(null)
        private set

    // 加载状态
    var isLoading by mutableStateOf(false)
        private set

    // 错误信息
    var errorMessage by mutableStateOf<String?>(null)
        private set

    // 提现记录列表（全部）
    var withdrawRecords by mutableStateOf<List<WithdrawRecord>>(emptyList())
        private set

    // 提现记录列表（申请中/待审核）
    var pendingRecords by mutableStateOf<List<WithdrawRecord>>(emptyList())
        private set

    // 提现记录列表（成功）
    var successRecords by mutableStateOf<List<WithdrawRecord>>(emptyList())
        private set

    // 提现记录加载状态
    var isLoadingRecords by mutableStateOf(false)
        private set

    init {
        println("WithdrawViewModel - 初始化提现页面") // 日志输出
        loadBalance()
        loadWithdrawRecords()  // 加载提现记录
    }

    /**
     * 加载用户余额
     */
    fun loadBalance() {
        scope.launch {
            val token = TokenManager.getToken()
            if (token.isNullOrEmpty()) {
                println("WithdrawViewModel - Token为空，无法加载余额") // 日志输出
                errorMessage = "未登录，无法获取余额"
                return@launch
            }

            println("WithdrawViewModel - 开始加载用户余额") // 日志输出
            isLoading = true
            errorMessage = null

            val result = repository.getUserBalance(token)

            if (result.isFailure) {
                val error = "获取余额失败: ${result.exceptionOrNull()?.message}"
                println("WithdrawViewModel - $error") // 日志输出
                errorMessage = error
                isLoading = false
                return@launch
            }

            val response = result.getOrNull()
            if (response?.code == 200 && response.data != null) {
                balance = response.data.balance
                println("WithdrawViewModel - 余额加载成功: ${response.data.balance}") // 日志输出
            } else {
                errorMessage = "获取余额失败: ${response?.message ?: "未知错误"}"
                println("WithdrawViewModel - $errorMessage") // 日志输出
            }

            isLoading = false
        }
    }

    /**
     * 申请提现
     */
    fun applyWithdraw(amount: String, onSuccess: () -> Unit, onError: (String) -> Unit) {
        scope.launch {
            val token = TokenManager.getToken()
            if (token.isNullOrEmpty()) {
                println("WithdrawViewModel - Token为空，无法申请提现") // 日志输出
                onError("未登录，无法申请提现")
                return@launch
            }

            // 验证金额
            val amountValue = amount.toDoubleOrNull()
            if (amountValue == null || amountValue <= 0) {
                println("WithdrawViewModel - 金额格式错误: $amount") // 日志输出
                onError("请输入有效金额")
                return@launch
            }

            // 验证是否为100的倍数
            if (amountValue % 100 != 0.0) {
                println("WithdrawViewModel - 金额不是100的倍数: $amountValue") // 日志输出
                onError("提现金额必须是100的倍数")
                return@launch
            }

            // 验证余额是否充足
            if (balance != null && amountValue > balance!!) {
                println("WithdrawViewModel - 余额不足，当前余额: $balance，提现金额: $amountValue") // 日志输出
                onError("余额不足")
                return@launch
            }

            println("WithdrawViewModel - 开始申请提现，金额: $amountValue") // 日志输出
            isLoading = true
            errorMessage = null

            // 调用提现接口
            val result = repository.applyWithdraw(token, amountValue)

            if (result.isFailure) {
                val error = "提现申请失败: ${result.exceptionOrNull()?.message}"
                println("WithdrawViewModel - $error") // 日志输出
                isLoading = false
                onError(error)
                return@launch
            }

            val response = result.getOrNull()
            println("WithdrawViewModel - 接口响应: code=${response?.code}, message=${response?.message}") // 日志输出

            if (response?.code == 200) {
                val successMsg = response.message?.removePrefix("Error: ") ?: "提现申请已提交"
                println("WithdrawViewModel - 提现申请成功: $successMsg") // 日志输出
                isLoading = false
                onSuccess()

                // 重新加载余额和记录
                loadBalance()
                loadWithdrawRecords()  // 使用真实接口
            } else {
                // 去除 "Error: " 前缀
                val error = response?.message?.removePrefix("Error: ") ?: "提现申请失败"
                println("WithdrawViewModel - 提现申请失败: $error") // 日志输出
                isLoading = false
                onError(error)
            }
        }
    }

    /**
     * 加载提现记录列表
     */
    fun loadWithdrawRecords() {
        scope.launch {
            val token = TokenManager.getToken()
            if (token.isNullOrEmpty()) {
                println("WithdrawViewModel - Token为空，无法加载提现记录") // 日志输出
                errorMessage = "未登录，无法获取提现记录"
                return@launch
            }

            println("WithdrawViewModel - 开始加载提现记录") // 日志输出
            isLoadingRecords = true

            val result = repository.getWithdrawalRecords(token)

            if (result.isFailure) {
                val error = "获取提现记录失败: ${result.exceptionOrNull()?.message}"
                println("WithdrawViewModel - $error") // 日志输出
                errorMessage = error
                isLoadingRecords = false
                return@launch
            }

            val response = result.getOrNull()
            println("WithdrawViewModel - 接口响应: code=${response?.code}, message=${response?.message}") // 日志输出

            if (response?.code == 200 && response.data != null) {
                withdrawRecords = response.data
                println("WithdrawViewModel - 提现记录加载成功，共 ${withdrawRecords.size} 条") // 日志输出

                // 打印前3条数据
                withdrawRecords.take(3).forEachIndexed { index, record ->
                    println("WithdrawViewModel - 记录${index + 1}: id=${record.id}, amount=${record.amount}, status=${record.status}, statusText=${record.statusText}") // 日志输出
                }

                // 按状态分组：0-待审核，1-成功
                pendingRecords = withdrawRecords.filter { it.status == 0 }
                successRecords = withdrawRecords.filter { it.status == 1 }

                println("WithdrawViewModel - 待审核: ${pendingRecords.size} 条，成功: ${successRecords.size} 条") // 日志输出
            } else {
                errorMessage = "获取提现记录失败: ${response?.message ?: "未知错误"}"
                println("WithdrawViewModel - $errorMessage") // 日志输出
            }

            isLoadingRecords = false
        }
    }

    /**
     * 清除错误信息
     */
    fun clearError() {
        errorMessage = null
    }
}